/*
 * BuroEcl
 * Copyright (c) 2023 Buroclic. All rights reserved.
 * 
 * This file corresponds to the form
 * allowing the user to make a deposit of documents
 * using its customer space
 */

// Imports
import React, { useEffect, useState } from 'react';
import PropTypes from "prop-types";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPaperPlane } from '@fortawesome/free-solid-svg-icons';

// Interfaces
import { BuroSecureSettings, PublicCabinet } from '../../interfaces/ICabinets';
import { Dossier } from '../../interfaces/IDossiers';

// Buroclic adaptive components
import { BCInput } from '../buroclic/BCInput';
import { ApiCallConstructor, HTTPResponse, fetchSentDocuments } from '../utils/ApiCallConstructor';
import { ErrorForUsr, signOut } from '../utils/Errors';
import { useNavigate } from 'react-router';
import { toast } from 'react-toastify';
import { DepositArea } from './subcomponents/DepositArea';

export const DocumentDeposit = (props) => {
    const navigate = useNavigate();
    const cabinet = (JSON.parse(localStorage.getItem('cabinet') || "{}") as PublicCabinet);
    const dossiers = props.dossiers as Dossier[] || [];

    const backBuroSecure = JSON.parse(localStorage.getItem('buroSecure') || '{}') as BuroSecureSettings;
    if (!backBuroSecure.enabled) navigate('/accueil', { state: {} })

    // Folder input
    const [inputDossierExpanded, setInputDossierExpanded] = useState<boolean>(false);
    const defaultDossier: Dossier = { DOS_REF: 0, DOS_CODE: '', DOS_TITRE: 'Nouveau dossier' } as Dossier;
    const [dossier, setDossier] = useState<Dossier>((props.dossier && props.dossier.DOS_REF > 0) ? props.dossier as Dossier : defaultDossier);

    // Form-related states/var
    const [subject, setSubject] = useState<string>('');
    const [files, setFiles] = useState<File[]>([]);
    const [comment, setComment] = useState<string>('');
    const [formError, setFormError] = useState<string>('');
    const [nbDepositDone, setNbDepositDone] = useState<number>(0);

    // Deposit Area settings
    const maximumFiles: number = backBuroSecure.maxFiles || 20;
    const allowedExtensions: { [key: string]: string[] } = {
        'image/*': ['.bmp', '.jpeg', '.jpg', '.png', '.gif', '.tif'],
        'application/msword': ['.doc', '.docx', '.docm', '.dotx', '.dotm', '.dot'],
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
        'application/vnd.oasis.opendocument.text': ['.odt'],
        'application/rtf': ['.rtf'],
        'application/pdf': ['.pdf'],
        'application/vnd.ms-excel': ['.xls'],
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx', '.xlsm', '.xlsb', '.xls', '.xltx', '.xltm', '.xlt', '.xps'],
        'application/vnd.oasis.opendocument.spreadsheet': ['.ods'],
        'text/csv': ['.csv'],
        'text/plain': ['.txt', '.md'],
        'application/xml': ['.xml']
    };



    useEffect(() => {
        setFormError('');
    }, [subject, files, dossier]);

    /**
     * Defines the code to be executed when the document deposit form is submit
     * which means preparing data to be sent do backend and send it
     * @author Mickaël "Naxe67" APPEL
     * @since 2024-04-18
     * @param e 
     * @returns {void}
     */
    const submitDeposit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        // Step 1 : Check for mandatory fields
        if (!subject || !files.length || !dossier) {
            setFormError('Veuillez renseigner l\'intégralité des champs nécessaires à l\'envoi des documents.');
            return;
        }

        try {
            // Step 2 : Format data inside multipart/form-data (which is better for files transfers)
            const formData = new FormData();
            formData.append('folder', dossier.DOS_REF.toString()); // DOS_REF
            formData.append('subject', subject); // DEP_OBJET
            formData.append('comment', comment); // DEP_COMMENT
            for (let i = 0; i < files.length; i++) formData.append('files[]', files[i]); // Documents


            // Step 3 : API Call to the backend in order to store documents and the deposit
            const submitDepositResponse: HTTPResponse = await toast.promise(
                ApiCallConstructor({
                    endpoint: '/me/deposits',
                    method: 'POST',
                    body: formData,
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    }
                }),
                {
                    pending: 'Envoi en cours...',
                    error: { render({ data }) { return `Erreur : ${(data as HTTPResponse).error}` } },
                    success: 'Envoi effectué avec succès'
                });

            console.log(submitDepositResponse);

            // Clear the form
            setDossier((props.dossier && props.dossier.DOS_REF > 0) ? props.dossier as Dossier : defaultDossier);
            console.log(props.dossier);
            setSubject('');
            setFiles([]);
            setComment('');
            setFormError('');
            setNbDepositDone(nbDepositDone + 1);

        } catch (err: any) {
            // Handle redirection for session expiration
            if (err.error === ErrorForUsr.NotSignedIn)
                signOut((cabAlias) => navigate(`/${cabAlias}/login`));

            console.error(err.error)
        }


        // Step 4 : Refresh sent documents stored in cache/localstorage
        const forceRefreshFromDB: boolean = true;
        fetchSentDocuments(forceRefreshFromDB).catch((err: any) => {
            // Handle redirection for session expiration
            if (err.error === ErrorForUsr.NotSignedIn)
                signOut((cabAlias: string) => navigate(`/${cabAlias}/login`));

            console.error(err.error)
        });
    };



    // JSX Template
    return (
        <div className={`flex flex-col my-3 mx-4 overflow-auto ${props.className ? props.className : ''}`}>
            {/* Document deposit form */}
            <form onSubmit={submitDeposit} className={`flex flex-col`} >
                {/* Title */}
                <div className="flex flex-row h-8 mb-4 place-items-center">
                    <div className="font-bold my-2 grow">
                        <span className='text-md lg:text-lg'><FontAwesomeIcon className={`w-6 h-6`} icon={faPaperPlane} style={{ color: cabinet.color + 'CF' }} /> Envoi d'un ou plusieurs documents</span>
                        <hr className={`mt-1 border-1 opacity-100 rounded-lg w-[30%]`} style={{ borderColor: cabinet.color }} />
                    </div>
                </div>

                <div className='flex flex-col lg:ml-[5%] lg:w-[90%]'>

                    {/* Folder */}
                    <label className="text-md mb-1 font-semibold inline-block bc-required">Dossier</label>
                    {/* Visible */}
                    <BCInput key={'input-dossier-' + (dossier.DOS_REF || '') + dossiers.length + inputDossierExpanded ? 'exp' : 'non'} className='w-full relative mb-2'
                        expanded={inputDossierExpanded} setExpanded={setInputDossierExpanded}
                        value={dossier} setValue={setDossier} defaultOption={defaultDossier} options={dossiers}
                        optionDisplayString={'{DOS_CODE - }{DOS_TITRE}'} />
                        
                    {/* Subject */}
                    <label htmlFor="subject" className="text-md mb-1 font-semibold inline-block bc-required">Objet</label>
                    <input type="text" id="subject" name="subject" placeholder='Objet' value={subject} onChange={(e) => { setSubject(e.target.value); }} maxLength={100}
                        className="border border-gray-300 bg-white py-2 px-2 text-md rounded-sm h-10 mb-2 shadow-bottom required:border-red-500" />

                    {/* File input */}
                    <div className="flex flex-col items-center justify-center w-full mb-2">
                        <span className='text-md mb-1 font-semibold inline-block mr-auto bc-required'>Document(s)</span>
                        <DepositArea key={`depositarea-${nbDepositDone}`} filesHandler={setFiles} maximum={maximumFiles} allowedExts={allowedExtensions} />
                    </div>


                    {/* Comment */}
                    <label htmlFor="comment" className="text-md mb-1 font-semibold inline-block">Commentaire</label>
                    <textarea id="comment" placeholder='Commentaire' value={comment} onChange={(e) => { setComment(e.target.value); setFormError(''); }} maxLength={2000}
                        className="border border-gray-300 bg-white py-2 px-2 text-md rounded-sm h-24 mb-2 shadow-bottom resize-none" />

                    <div className='italic text-sm'><span className='bc-required'></span> Champs obligatoires<span className='float-right text-base text-red-500 not-italic'>{formError}</span></div>

                    {/* Submit */}
                    <button className={`text-white text-1xl font-bold py-2 px-12 w-[25%] rounded-lg shadow-md ml-auto`} style={{ backgroundColor: cabinet.color ? cabinet.color : 'orange' }} type="submit">
                        Envoyer
                    </button>
                </div>
            </form >
        </div >
    );
};

DocumentDeposit.propTypes = {
    dossiers: PropTypes.arrayOf(PropTypes.object), // Available folders 
    dossier: PropTypes.object, // Folder to prefill
    className: PropTypes.string
}